import { reduce } from 'lodash-es';

import { setStyleProperties } from '@/util';

import {
  TRACK_TYPE,
  type VBaseTrackProps,
  type VBaseTrackType,
} from '../VBaseTrack';
import {
  SLIDER_MARKER_GAP,
  SLIDER_MARKER_SIZE,
  SLIDER_TRACK_MARKER_GAP,
  SLIDER_TRACK_MARKER_SIZE,
  SLIDER_VARIANT,
} from './constant';
import type {
  VSliderContextValue,
  VSliderMarkerItem,
  VSliderNormalizedMarkerItem,
  VSliderProps,
} from './type';

export const calculateSliderTrackWidth = (
  max: number,
  min: number,
  step: number,
) => {
  const length = Math.floor((max - min) / step) + 1;

  return (
    SLIDER_TRACK_MARKER_SIZE * length +
    SLIDER_TRACK_MARKER_GAP * (length - 1) +
    8
  );
};

export const changeFilledTrackEl = ({
  endLeft,
  startLeft,
  $filledTrack,
  variant,
}: {
  endLeft?: number;
  startLeft?: number;
  $filledTrack: HTMLDivElement;
  variant?: VSliderProps['variant'];
}) => {
  const isFrameSliderVariant = variant === SLIDER_VARIANT.FRAME_SLIDER;
  const isFrameTrackSliderVariant =
    variant === SLIDER_VARIANT.FRAME_TRACK_SLIDER;

  if (startLeft != null) {
    startLeft =
      isFrameSliderVariant || isFrameTrackSliderVariant
        ? startLeft - 2
        : startLeft;

    setStyleProperties($filledTrack, {
      '--startLeft': `${startLeft}`,
    });
  }

  if (endLeft != null) {
    endLeft =
      isFrameSliderVariant || isFrameTrackSliderVariant ? endLeft + 2 : endLeft;

    setStyleProperties($filledTrack, {
      '--endLeft': `${endLeft}`,
    });
  }

  startLeft ??= +$filledTrack.style.getPropertyValue('--startLeft');
  endLeft ??= +$filledTrack.style.getPropertyValue('--endLeft');
};

export const changeAllFilledTrack = ({
  normalizedMarkerItemMap,
  max,
  min,
  step,
  type,
  variant,
  $track,
}: {
  normalizedMarkerItemMap: Dictionary<VSliderNormalizedMarkerItem>;
  max: number;
  min: number;
  step: number;
  type: VBaseTrackType;
  variant: VSliderProps<any>['variant'];
  $track: React.RefObject<Nullish<HTMLDivElement>>;
}) => {
  getFilledTrackElList($track).forEach($filledTrack => {
    const startValue = $filledTrack.ariaValueMin;
    const endValue = $filledTrack.ariaValueMax;

    if (startValue == null || endValue == null) return;

    const safeEndValue = getSafeValue({
      max: getNonHiddenMax({ max, normalizedMarkerItemMap }),
      step,
      value: +endValue,
    });
    const endValueTrackLeft = getLeft({
      gap: SLIDER_TRACK_MARKER_GAP,
      min,
      size: SLIDER_TRACK_MARKER_SIZE,
      step,
      value: safeEndValue,
    });
    const startValueTrackLeft = getLeft({
      gap: SLIDER_TRACK_MARKER_GAP,
      min,
      size: SLIDER_TRACK_MARKER_SIZE,
      step,
      value: +startValue,
    });

    const startLeft =
      startValueTrackLeft +
      (type !== TRACK_TYPE.NORMAL
        ? SLIDER_TRACK_MARKER_SIZE / 2
        : -SLIDER_TRACK_MARKER_SIZE);

    const endLeft = endValueTrackLeft + SLIDER_TRACK_MARKER_SIZE / 2;

    setStyleProperties($filledTrack, {
      '--endOrgLeft': `${endLeft}`,
      '--startOrgLeft': `${startLeft}`,
    });

    changeFilledTrackEl({ $filledTrack, endLeft, startLeft, variant });
  });
};

export const createMarkerBuffer = ({
  fillValue = 0,
  max,
  min,
  padStart = 0,
}: Required<Pick<VSliderProps, 'max' | 'min'>> & {
  fillValue?: number;
  padStart?: number;
}) => {
  const markerBuffer = new Float32Array(max - min + 1 + padStart);

  markerBuffer.fill(fillValue);

  for (let i = min; i <= max; i++) {
    markerBuffer[i - min + padStart] = i;
  }

  if (markerBuffer[max - min + padStart] !== max) {
    markerBuffer[max - min + padStart] = max;
  }

  return markerBuffer;
};

export const createNormalizedMarkerItem = (
  markerItem: VSliderMarkerItem & { hidden?: boolean },
  { min, step }: Required<Pick<VSliderProps, 'min' | 'step'>>,
): VSliderNormalizedMarkerItem => {
  const value = markerItem.value;
  const hidden =
    markerItem.hidden == null ? !!((value - min) % step) : markerItem.hidden;

  return {
    ...markerItem,
    hidden,
    left: {
      track: getTrackMarkerLeft({ min, step, value }),
      marker: getMarkerLeft({ min, step, value }) - 4,
    },
    skippedMarker: hidden,
  };
};

export const getLeft = ({
  gap,
  min,
  size,
  step,
  value,
}: { gap: number; size: number; value: number } & Required<
  Pick<VSliderProps, 'min' | 'step'>
>) => ((value - min) * (size + gap)) / step + 4;

export const createGetLeft =
  ({ gap, size }: Pick<Parameters<typeof getLeft>[0], 'gap' | 'size'>) =>
  (param: Omit<Parameters<typeof getLeft>[0], 'gap' | 'size'>) =>
    getLeft({ ...param, gap, size });

export const getTrackMarkerLeft = createGetLeft({
  gap: SLIDER_TRACK_MARKER_GAP,
  size: SLIDER_TRACK_MARKER_SIZE,
});

export const getMarkerLeft = createGetLeft({
  gap: SLIDER_MARKER_GAP,
  size: SLIDER_MARKER_SIZE,
});

export const getNonHiddenMax = ({
  max,
  normalizedMarkerItemMap,
}: {
  max: number;
  normalizedMarkerItemMap: Dictionary<VSliderNormalizedMarkerItem>;
}) => {
  const nonHiddenMax = normalizedMarkerItemMap
    ? Object.values(normalizedMarkerItemMap).reduce(
        (acc, v) => (v.hidden ? acc : v.value >= acc ? v.value : acc),
        -1,
      )
    : max;

  return nonHiddenMax;
};

export const getClosestMarkerItem = ({
  markerItemMap,
  x,
}: {
  markerItemMap: Dictionary<VSliderNormalizedMarkerItem>;
  x: number;
}) => {
  const closestMarkerItem = reduce(markerItemMap, (curMin, cur) => {
    if (cur.skippedMarker) return curMin;

    const curMinDiff = Math.abs(
      x - (curMin.left.track + SLIDER_TRACK_MARKER_SIZE),
    );
    const curDiff = Math.abs(x - cur.left.track);
    return curMinDiff > curDiff ? cur : curMin;
  });

  return closestMarkerItem!;
};

export const getFilledTrackElByValue = <T extends HTMLElement>(
  $track: React.RefObject<Nullish<T>>,
  value: number,
) => {
  if (!$track.current) return;

  const $filledTrack =
    $track.current.querySelector<HTMLDivElement>(
      `[aria-valuemax="${value}"]`,
    ) ||
    $track.current.querySelector<HTMLDivElement>(`[aria-valuemin="${value}"]`);

  return $filledTrack;
};

export const getFilledTrackElList = <T extends HTMLElement>(
  $track: React.RefObject<Nullish<T>>,
) =>
  Array.from(
    $track.current?.querySelectorAll<HTMLDivElement>(
      '[aria-label="v-slider-filled-track"]',
    ) ?? [],
  );

export const getSafeValue = ({
  max,
  step,
  value,
}: {
  max: number;
  step: number;
  value: number;
}) =>
  value !== max
    ? Math.min(value, max)
    : !(value % step)
      ? value
      : Math.floor(value / step) * step;

export const createSliderValueUpdater = ({
  normalizedValue,
  min,
  setValue,
  type,
}: Pick<
  VSliderContextValue<any>,
  'normalizedValue' | 'min' | 'setValue' | 'type'
>) => {
  return (markerItemValue: number, targetValue: number = markerItemValue) => {
    const [closestValue = [min], closestIndex = 0] = normalizedValue.reduce(
      ([curMin, curMinIndex], cur, index) => {
        const lastCurMin = curMin[1];
        const firstCur = cur[0];

        const [curMinDiff, curDiff] = [
          Math.abs(lastCurMin - targetValue),
          Math.abs(firstCur - targetValue),
        ];

        return curMinDiff <= curDiff ? [curMin, curMinIndex] : [cur, index];
      },
      [normalizedValue[0], 0] as [number[], number],
    );

    const [start, end = start] = closestValue;
    const [diffStart, diffEnd] = [
      Math.abs(targetValue - start),
      Math.abs(targetValue - end),
    ];

    let newValue: number | [number, number] | [number, number][] =
      markerItemValue;

    switch (type) {
      case TRACK_TYPE.MULTIPLE_RANGE:
        newValue = normalizedValue.map((_, i) =>
          i !== closestIndex
            ? _
            : diffStart <= diffEnd
              ? [markerItemValue, end]
              : [start, markerItemValue],
        ) as [number, number][];
        break;
      case TRACK_TYPE.RANGE:
        newValue =
          diffStart <= diffEnd
            ? [markerItemValue, end]
            : [start, markerItemValue];
        break;
    }

    setValue(newValue);
  };
};

export const normalizeNonOverlappingRange = (
  value: [number, number][],
  { min, step }: { min: number; step: number },
): [number, number][] => {
  if (!value.length) return [[min, min]];

  const sorted = value
    .map(([start, end]) => [start, end].toSorted((a, b) => a - b))
    .toSorted((a, b) => a[0] - b[0]) as [number, number][];

  const result: [number, number][] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prevRange = result[result.length - 1];
    const [currStart, currEnd] = sorted[i];

    const minValidStart = prevRange[1] + step;

    if (currStart <= prevRange[1]) {
      if (currEnd > prevRange[1]) {
        result.push([minValidStart, Math.max(currEnd, minValidStart)]);
      }
    } else {
      result.push([currStart, currEnd]);
    }
  }

  return result;
};

export const makeMarkerItem = (
  item: VSliderMarkerItem,
  { min, step }: { min: number; step: number },
) => createNormalizedMarkerItem(item, { min, step });

export const makeNormalizedMarkerItemMap = ({
  $container,
  markerBuffer,
  marker,
  min,
  step,
  renderTrigger,
  visibleRangeOffsetRef,
}: {
  $container: React.RefObject<Nullish<HTMLDivElement>>;
  markerBuffer: Float32Array;
  marker: VSliderProps['marker'];
  min: number;
  step: number;
  renderTrigger: Nullish<number>;
  visibleRangeOffsetRef: React.RefObject<[number, number]>;
}) => {
  if (!$container.current || !renderTrigger || !marker || step < 0) return null;

  const bufferLen = markerBuffer.length;
  const scrollWidth = $container.current.scrollWidth;

  const startIndex = Math.max(
    0,
    Math.floor(
      (visibleRangeOffsetRef.current[0] / scrollWidth) * (bufferLen - 1),
    ),
  );

  const endIndex = Math.min(
    bufferLen - 1,
    Math.ceil(
      (visibleRangeOffsetRef.current[1] / scrollWidth) * (bufferLen - 1),
    ),
  );

  const result = {} as Dictionary<VSliderNormalizedMarkerItem>;

  for (let index = startIndex; index <= endIndex; index++) {
    const value = markerBuffer[index];
    const key = index + min;
    const markerItem = makeMarkerItem(
      { label: `${key}`, value },
      { min, step },
    );

    result[key] = markerItem;
  }

  if (marker === true) return result;

  if (Array.isArray(marker)) {
    const markerItemMap = marker.reduce((acc, item) => {
      const markerItem = makeMarkerItem(item, { min, step });

      markerItem.hidden = false;

      return { ...acc, [item.value]: markerItem };
    }, {} as Dictionary<VSliderNormalizedMarkerItem>);

    Object.entries(result).forEach(([v]) => {
      if (markerItemMap[v]) result[v] = markerItemMap[v];
      else result[v].hidden = true;
    });
  } else {
    Object.entries(result).forEach(([v]) => {
      if (marker[v]) {
        result[v] = makeMarkerItem(marker[v], { min, step });
        result[v].hidden = false;
      }
    });
  }

  return result;
};

export const makeNormalizedValue = <T extends VBaseTrackType>({
  type,
  value,
  min,
  step,
}: {
  type: T;
  value: VBaseTrackProps<T>['value'];
  min: number;
  step: number;
}) => {
  switch (type) {
    case TRACK_TYPE.MULTIPLE_RANGE:
      return Array.isArray(value) && value.length
        ? normalizeNonOverlappingRange(value as [number, number][], {
            min,
            step,
          })
        : [];
    case TRACK_TYPE.RANGE:
      return Array.isArray(value) && value.length
        ? value.length === 1
          ? [
              [(value as unknown as [number, number])[0], min].toSorted(
                (a, b) => a - b,
              ),
            ]
          : [
              (value as [number, number])
                .map(v => (v == null ? min : v))
                .toSorted((a, b) => a - b),
            ]
        : [];
    default:
      return typeof value === 'number' ? [[value]] : [[min]];
  }
};
